import { Button, Divider, Popover, Radio, RadioChangeEvent, Spin, Tag, Typography } from 'antd'
import { Filter, FilterTypes } from 'components/FilterDrawer/FilterDrawer.config'
import { format } from 'date-fns'
import React from 'react'
import { EMPLOYEES_EXPORT_TYPES, type EmployeesExportType } from 'services/employeesApi'

const { Text } = Typography

interface ExportPopoverProps {
  open: boolean
  onClose: () => void
  onExport: (exportType: EmployeesExportType) => void
  activeFilters: Filter[]
  searchValue: string
  exportCount: number | null
  isCountLoading: boolean
  exportType: EmployeesExportType
  onExportTypeChange: (exportType: EmployeesExportType) => void
  children: React.ReactElement
}

const formatFilterValue = (value: string, type?: FilterTypes): string => {
  if (type === FilterTypes.DATE_BIT) {
    try {
      return format(new Date(value), 'MMMM yyyy')
    } catch {
      return value
    }
  }
  return value
}

const getActiveFilterEntries = (filters: Filter[]): { label: string; values: string[]; type?: FilterTypes }[] =>
  filters
    .filter(f => f.value.length > 0)
    .map(f => ({
      label: f.activeFilterLabel || f.label || f.name,
      values: f.value,
      type: f.type,
    }))

const ExportPopover = ({
  open,
  onClose,
  onExport,
  activeFilters,
  searchValue,
  exportCount,
  isCountLoading,
  exportType,
  onExportTypeChange,
  children,
}: ExportPopoverProps) => {
  const filterEntries = getActiveFilterEntries(activeFilters)
  const hasFiltersOrSearch = filterEntries.length > 0 || searchValue.trim().length > 0

  const handleTypeChange = (event: RadioChangeEvent) => {
    onExportTypeChange(event.target.value as EmployeesExportType)
  }

  const content = (
    <div style={{ minWidth: 320, maxWidth: 420 }}>
      <div style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 13, color: '#7E8BA6', display: 'block', marginBottom: 8 }}>Export type</Text>
        <Radio.Group value={exportType} onChange={handleTypeChange} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Radio value={EMPLOYEES_EXPORT_TYPES.SUMMARY}>
            <Text style={{ fontSize: 13, color: '#1c2842' }}>Summary</Text>
            <Text style={{ fontSize: 12, color: '#7E8BA6', display: 'block' }}>One row per employee</Text>
          </Radio>
          <Radio value={EMPLOYEES_EXPORT_TYPES.DETAILED}>
            <Text style={{ fontSize: 13, color: '#1c2842' }}>Detailed</Text>
            <Text style={{ fontSize: 12, color: '#7E8BA6', display: 'block' }}>All positions / role history</Text>
          </Radio>
        </Radio.Group>
      </div>

      {hasFiltersOrSearch ? (
        <div style={{ marginBottom: 16 }}>
          <Text style={{ fontSize: 13, color: '#7E8BA6', display: 'block', marginBottom: 8 }}>Active filters</Text>

          {filterEntries.map(({ label, values, type }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4, flexWrap: 'wrap' }}>
              <Text strong style={{ fontSize: 13, color: '#1c2842' }}>
                {label}:
              </Text>
              {type === FilterTypes.BOOL ? (
                <Tag color='blue'>Yes</Tag>
              ) : (
                values.map(v => (
                  <Tag key={v} color='blue'>
                    {formatFilterValue(v, type)}
                  </Tag>
                ))
              )}
            </div>
          ))}

          {searchValue.trim().length > 0 && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
              <Text strong style={{ fontSize: 13, color: '#1c2842' }}>
                Search:
              </Text>
              <Tag color='blue'>{searchValue.trim()}</Tag>
            </div>
          )}
        </div>
      ) : (
        <Text style={{ fontSize: 13, color: '#7E8BA6', display: 'block', marginBottom: 16 }}>
          No filters applied — all employees will be exported.
        </Text>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#F5F7FA',
          borderRadius: 8,
          padding: '10px 14px',
          marginBottom: 16,
        }}
      >
        <Text style={{ fontSize: 14, color: '#1c2842' }}>Employees to export</Text>
        {isCountLoading ? (
          <Spin size='small' />
        ) : (
          <Text strong style={{ fontSize: 14, color: '#1c2842' }}>
            {exportCount !== null ? exportCount.toLocaleString() : '—'}
          </Text>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type='primary'
          onClick={() => {
            onExport(exportType)
            onClose()
          }}
          disabled={isCountLoading || exportCount === 0}
        >
          Export
        </Button>
      </div>
    </div>
  )

  return (
    <Popover
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose()
      }}
      content={content}
      title={
        <>
          <Text style={{ fontFamily: 'Dosis', fontWeight: 600, fontSize: 16, color: '#1c2842' }}>
            Export Employees CSV
          </Text>
          <Divider style={{ margin: '10px 0' }} />
        </>
      }
      trigger='click'
      placement='bottomRight'
      arrow={false}
      overlayInnerStyle={{ borderRadius: 12, padding: '16px 20px' }}
    >
      {children}
    </Popover>
  )
}

export default ExportPopover
